import { useState, useEffect } from 'react';
import { fetchMainCredit, updateMainCredit } from '@/lib/api';
import { formatMXN, formatPercent, formatDate } from '@/lib/formatters';
import { PageHeader } from '@/components/common/PageHeader';
import { KPICard } from '@/components/common/KPICard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  CreditCard, DollarSign, TrendingDown, Calendar,
  ArrowDown, Pencil, Check, X as XIcon, Building2,
  ArrowRight, Shield, TrendingUp, Percent,
} from 'lucide-react';

export default function Credito() {
  const [credit, setCredit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});

  const load = async () => {
    try { setCredit(await fetchMainCredit()); }
    catch { toast.error('Error al cargar crédito'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startEdit = () => {
    setForm({
      current_balance: credit.current_balance,
      annual_rate: credit.annual_rate,
      start_date: credit.start_date || '',
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    try {
      const updated = await updateMainCredit({
        current_balance: parseFloat(form.current_balance) || 0,
        annual_rate: parseFloat(form.annual_rate) || 0,
        start_date: form.start_date || null,
      });
      setCredit(updated);
      setEditing(false);
      toast.success('Crédito actualizado');
    } catch { toast.error('Error al actualizar'); }
  };

  if (loading) return (
    <div className="space-y-6">
      <Skeleton className="h-10 w-48" />
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">{[1,2,3,4].map(i => <Skeleton key={i} className="h-28 rounded-xl" />)}</div>
      <Skeleton className="h-60 rounded-xl" />
      <Skeleton className="h-40 rounded-xl" />
    </div>
  );

  const c = credit;

  return (
    <div className="space-y-6" data-testid="credito-page">
      <PageHeader title="Crédito Principal" subtitle="Crédito puente para financiamiento del desarrollo">
        {!editing && (
          <Button variant="outline" size="sm" onClick={startEdit} data-testid="credito-edit-button">
            <Pencil className="h-3.5 w-3.5 mr-1.5" /> Editar Parámetros
          </Button>
        )}
      </PageHeader>

      {/* ── KPI Cards ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <KPICard label="Saldo Original" value={formatMXN(c.current_balance)} icon={CreditCard} compact testId="credito-saldo-original" />
        <KPICard label="Reducción Aplicada" value={formatMXN(c.total_liberado)} icon={ArrowDown} subtitle={`${c.count_liberaciones_aplicadas} movimientos`} compact testId="credito-liberaciones" />
        <KPICard
          label="Saldo Actual"
          value={formatMXN(c.saldo_ajustado)}
          icon={Shield}
          subtitle={`${c.pct_reducido || 0}% reducido`}
          compact testId="credito-saldo-ajustado"
          className={c.saldo_ajustado < c.current_balance ? 'border-emerald-200' : ''}
        />
        <KPICard label="Interés Mensual" value={formatMXN(c.interes_sobre_ajustado)} icon={TrendingDown} subtitle={`Tasa: ${c.annual_rate}% anual`} compact testId="credito-interes" />
      </div>

      {/* ── Credit Parameters (Edit mode) ── */}
      {editing && (
        <Card className="rounded-xl border-2 border-[#F05A28]/30 bg-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <Pencil className="h-4 w-4 text-[#F05A28]" /> Editar Parámetros del Crédito
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1.5 block">Saldo del Crédito (MXN)</label>
                <Input type="number" value={form.current_balance} onChange={e => setForm(f => ({ ...f, current_balance: e.target.value }))} data-testid="credito-edit-balance" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1.5 block">Tasa Anual (%)</label>
                <Input type="number" step="0.01" value={form.annual_rate} onChange={e => setForm(f => ({ ...f, annual_rate: e.target.value }))} data-testid="credito-edit-rate" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1.5 block">Fecha Inicio</label>
                <Input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} data-testid="credito-edit-date" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" size="sm" onClick={() => setEditing(false)}><XIcon className="h-3.5 w-3.5 mr-1" /> Cancelar</Button>
              <Button size="sm" onClick={saveEdit} data-testid="credito-save-button"><Check className="h-3.5 w-3.5 mr-1" /> Guardar</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Financial Breakdown ── */}
      <Card className="rounded-xl border bg-white">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <Percent className="h-4 w-4 text-[#F05A28]" /> Desglose de Intereses
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Visual saldo bar */}
            <div className="bg-slate-50 rounded-xl p-4" data-testid="credito-saldo-bar">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-slate-500">Saldo del crédito</span>
                <span className="font-semibold tabular-nums">{formatMXN(c.current_balance)}</span>
              </div>
              <div className="relative h-7 rounded-lg bg-slate-200 overflow-hidden">
                {/* Liberado portion */}
                <div
                  className="absolute inset-y-0 left-0 bg-emerald-400 transition-all duration-500"
                  style={{ width: `${c.pct_reducido || 0}%` }}
                />
                {/* Projected portion */}
                <div
                  className="absolute inset-y-0 bg-emerald-200 transition-all duration-500"
                  style={{ left: `${c.pct_reducido || 0}%`, width: `${Math.min((c.pct_total || 0) - (c.pct_reducido || 0), 100 - (c.pct_reducido || 0))}%` }}
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-xs font-bold text-white drop-shadow-sm">
                    {c.pct_reducido || 0}% reducido
                  </span>
                </div>
              </div>
              <div className="flex justify-between mt-2 text-[10px]">
                <div className="flex items-center gap-1.5">
                  <div className="h-2 w-2 rounded-full bg-emerald-400" />
                  <span className="text-slate-500">Liberado: {formatMXN(c.total_liberado)}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="h-2 w-2 rounded-full bg-emerald-200" />
                  <span className="text-slate-500">Proyectado: {formatMXN(c.total_liberaciones_proyectadas)}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="h-2 w-2 rounded-full bg-slate-300" />
                  <span className="text-slate-500">Pendiente: {formatMXN(c.saldo_futuro_proyectado)}</span>
                </div>
              </div>
            </div>

            {/* Interest calculation table */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="rounded-lg border p-4 space-y-3">
                <p className="text-xs font-semibold text-slate-500 uppercase">Cálculo de Interés</p>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Tasa anual</span>
                    <span className="font-medium tabular-nums">{c.annual_rate}%</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Tasa mensual</span>
                    <span className="font-medium tabular-nums">{c.tasa_mensual.toFixed(4)}%</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Interés s/saldo original</span>
                    <span className="font-medium tabular-nums text-slate-400 line-through">{formatMXN(c.interes_sobre_original)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600 font-medium">Interés s/saldo ajustado</span>
                    <span className="font-bold tabular-nums text-[#F05A28]">{formatMXN(c.interes_sobre_ajustado)}</span>
                  </div>
                  <div className="flex justify-between text-sm bg-emerald-50 rounded px-2 py-1">
                    <span className="text-emerald-700 text-xs font-medium">Ahorro mensual por liberaciones</span>
                    <span className="font-bold tabular-nums text-emerald-700">{formatMXN(c.ahorro_mensual_intereses)}</span>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border p-4 space-y-3">
                <p className="text-xs font-semibold text-slate-500 uppercase">Datos del Crédito</p>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Fecha inicio</span>
                    <span className="font-medium">{formatDate(c.start_date)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Saldo original</span>
                    <span className="font-medium tabular-nums">{formatMXN(c.current_balance)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Total liberado</span>
                    <span className="font-medium tabular-nums text-emerald-600">−{formatMXN(c.total_liberado)}</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-700 font-semibold">Saldo ajustado</span>
                    <span className="font-bold tabular-nums text-slate-900">{formatMXN(c.saldo_real || c.saldo_ajustado)}</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Liberaciones pendientes</span>
                    <span className="font-medium tabular-nums text-slate-400">−{formatMXN(c.total_liberaciones_proyectadas)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Saldo futuro proyectado</span>
                    <span className="font-medium tabular-nums">{formatMXN(c.saldo_futuro_proyectado)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Liberaciones Aplicadas ── */}
      <Card className="rounded-xl border bg-white overflow-hidden">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <Building2 className="h-4 w-4 text-emerald-600" />
            Liberaciones Aplicadas
            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] ml-1">{c.count_liberaciones_aplicadas}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {c.liberaciones_aplicadas?.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-6">Sin liberaciones aplicadas</p>
          ) : (
            <Table data-testid="liberaciones-aplicadas-table">
              <TableHeader>
                <TableRow className="bg-slate-50/80">
                  <TableHead className="text-xs uppercase py-2 px-4 font-semibold text-slate-500">Propiedad</TableHead>
                  <TableHead className="text-xs uppercase py-2 px-4 font-semibold text-slate-500">Lt-Mz</TableHead>
                  <TableHead className="text-xs uppercase py-2 px-4 font-semibold text-slate-500">Tipo</TableHead>
                  <TableHead className="text-xs uppercase py-2 px-4 font-semibold text-slate-500">Fecha</TableHead>
                  <TableHead className="text-xs uppercase py-2 px-4 font-semibold text-slate-500 text-right">Monto</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {c.liberaciones_aplicadas.map((l, idx) => (
                  <TableRow key={`${l.property_id}-${l.payment_id || idx}`} className="table-row-hover">
                    <TableCell className="py-2 px-4 text-sm font-medium">{l.name}</TableCell>
                    <TableCell className="py-2 px-4 text-sm text-slate-500">{l.lot}-{l.block}</TableCell>
                    <TableCell className="py-2 px-4">
                      <Badge variant="outline" className={l.tipo === 'liberación' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-blue-50 text-blue-700 border-blue-200'}>
                        {l.tipo === 'liberación' ? 'Liberación' : 'Pago a crédito'}
                      </Badge>
                    </TableCell>
                    <TableCell className="py-2 px-4 text-sm text-slate-500">{formatDate(l.release_date || l.date)}</TableCell>
                    <TableCell className="py-2 px-4 text-right tabular-nums text-sm font-semibold text-emerald-700">{formatMXN(l.release_amount || l.amount)}</TableCell>
                  </TableRow>
                ))}
                <TableRow className="bg-slate-50/80">
                  <TableCell className="py-2 px-4 text-xs font-semibold text-slate-600" colSpan={4}>Total Reducción Aplicada</TableCell>
                  <TableCell className="py-2 px-4 text-right tabular-nums text-sm font-bold text-emerald-700">{formatMXN(c.total_liberado)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* ── Liberaciones Proyectadas ── */}
      {c.liberaciones_proyectadas?.length > 0 && (
        <Card className="rounded-xl border bg-white overflow-hidden">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-blue-500" />
              Liberaciones Proyectadas
              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-[10px] ml-1">{c.count_liberaciones_proyectadas}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table data-testid="liberaciones-proyectadas-table">
              <TableHeader>
                <TableRow className="bg-slate-50/80">
                  <TableHead className="text-xs uppercase py-2 px-4 font-semibold text-slate-500">Propiedad</TableHead>
                  <TableHead className="text-xs uppercase py-2 px-4 font-semibold text-slate-500">Lt-Mz</TableHead>
                  <TableHead className="text-xs uppercase py-2 px-4 font-semibold text-slate-500">Estatus</TableHead>
                  <TableHead className="text-xs uppercase py-2 px-4 font-semibold text-slate-500 text-right">Liberación</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {c.liberaciones_proyectadas.map(l => (
                  <TableRow key={l.property_id} className="table-row-hover">
                    <TableCell className="py-2 px-4 text-sm font-medium">{l.name}</TableCell>
                    <TableCell className="py-2 px-4 text-sm text-slate-500">{l.lot}-{l.block}</TableCell>
                    <TableCell className="py-2 px-4">
                      <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                        {l.property_status}
                      </Badge>
                    </TableCell>
                    <TableCell className="py-2 px-4 text-right tabular-nums text-sm font-medium text-slate-600">{formatMXN(l.release_amount)}</TableCell>
                  </TableRow>
                ))}
                <TableRow className="bg-slate-50/80">
                  <TableCell className="py-2 px-4 text-xs font-semibold text-slate-600" colSpan={3}>Total Proyectado</TableCell>
                  <TableCell className="py-2 px-4 text-right tabular-nums text-sm font-bold text-blue-700">{formatMXN(c.total_liberaciones_proyectadas)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
